import React, { useState } from "react";
import { TrashIcon } from "@heroicons/react/solid";
import Resizer from "react-image-file-resizer";
import { FilePreviewModal } from "./FilePreviewModal";
import { apiFetch } from "../utils/apiClient";
import { Eye, Paperclip } from "lucide-react";
const Files = ({
  renderError,
  formData,
  setFormData,
  section,
  isReadOnly,
  user,
  page,
}) => {
  const [previewUrls, setPreviewUrls] = useState({});
  const [showPopup, setShowPopup] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileUrl = selectedFile ? URL.createObjectURL(selectedFile) : "";
  const [fileUploadError, setFileUploadError] = useState("");

  const handleFileUpload = async (e) => {
    const MAX_FILE_SIZE_MB = 5;
    const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;
    const files = Array.from(e.target.files) || [];

    const ALLOWED_TYPES = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];

    const invalidFiles = files.filter(
      (file) => !ALLOWED_TYPES.includes(file.type),
    );

    if (invalidFiles.length > 0) {
      setFileUploadError("Formats acceptés : PDF, JPG, JPEG, PNG.");
      return;
    }

    const resizeImage = (file) =>
      new Promise((resolve) => {
        if (!file.type.includes("image")) return resolve(file);

        Resizer.imageFileResizer(
          file,
          1600,
          1600,
          "WEBP",
          70,
          0,
          (uri) => {
            const resizedFile = new File([uri], file.name, {
              type: "image/webp",
            });
            resolve(resizedFile);
          },
          "blob",
          200,
          200,
        );
      });

    const compressPDF = async (file) => {
      if (file.type !== "application/pdf") return file;

      // Conserver les octets originaux afin de ne pas altérer les PDF
      // protégés par mot de passe, signés ou contenant des formulaires.
      // Le fichier sera ensuite chiffré par le backend avant son stockage.
      return file;
    };
    const processFile = async (file) => {
      if (file.type.includes("image")) {
        return await resizeImage(file);
      } else if (file.type === "application/pdf") {
        return await compressPDF(file);
      }

      return file;
    };

    const processedFiles = await Promise.all(files.map(processFile));

    const oversizedFiles = processedFiles.filter(
      (file) => file.size > MAX_FILE_SIZE,
    );

    if (oversizedFiles.length > 0) {
      setFileUploadError(
        `Le fichier (même après compression) dépasse ${MAX_FILE_SIZE_MB} Mo.`,
      );
      return;
    }

    setFileUploadError("");

    let newFormData = { ...formData };
    let newPreviewUrls = { ...previewUrls };

    const filterDuplicates = (existingFiles = [], newFiles) => {
      const existingFileNames = new Set(
        existingFiles.map((f) => f.name || f.filename),
      );

      const filteredFiles = newFiles.filter(
        (f) => !existingFileNames.has(f.name),
      );

      if (filteredFiles.length < newFiles.length) {
        setFileUploadError("Un fichier avec ce nom existe déjà.");
      } else {
        setFileUploadError("");
      }

      return filteredFiles;
    };

    let existing = [];
    let filtered = [];

    if (section === "Visa du Conseil") {
      existing = newFormData.visaConseil?.files || [];
      filtered = filterDuplicates(existing, processedFiles);
    } else if (section === "407 ETR") {
      existing = newFormData.ETRFiles || [];
      filtered = filterDuplicates(existing, processedFiles);
    } else if (section === "formation") {
      existing = newFormData.formation?.files || [];
      filtered = filterDuplicates(existing, processedFiles);
    }

    // Upload vers le backend
    if (filtered.length > 0) {
      const uploadFormData = new FormData();

      uploadFormData.append("numeroFormulaire", formData.numeroFormulaire);

      uploadFormData.append("section", section);

      filtered.forEach((file) => {
        uploadFormData.append("files", file);
      });

      const response = await apiFetch(
        `${process.env.REACT_APP_API_BASE_URL}/uploadFiles`,
        {
          method: "POST",
          body: uploadFormData,
        },
      );

      if (!response.ok) {
        throw new Error("Erreur lors du téléversement");
      }

      const data = await response.json();

      filtered.forEach((file) => {
        const uploaded = data.uploadedFiles.find(
          (f) => f.filename === file.name,
        );

        if (uploaded) {
          file._id = uploaded._id;
        }
      });

      if (section === "Visa du Conseil") {
        newFormData.visaConseil = {
          ...newFormData.visaConseil,
          files: [...existing, ...filtered],
          fileInfos: [
            ...(newFormData.visaConseil?.fileInfos || []),
            ...data.uploadedFiles.map((f) => f.fileInfo),
          ],
        };
      } else if (section === "407 ETR") {
        newFormData.ETRFiles = [...existing, ...filtered];
        newFormData.ETRFileInfos = [
          ...(newFormData.ETRFileInfos || []),
          ...data.uploadedFiles.map((f) => f.fileInfo),
        ];
      } else if (section === "formation") {
        newFormData.formation = {
          ...newFormData.formation,
          files: [...existing, ...filtered],
          fileInfos: [
            ...(newFormData.formation?.fileInfos || []),
            ...data.uploadedFiles.map((f) => f.fileInfo),
          ],
        };
      }

      filtered.forEach((file) => {
        newPreviewUrls[file.name] = URL.createObjectURL(file);
      });
    }

    setPreviewUrls(newPreviewUrls);
    setFormData(newFormData);
  };

const handleFileDelete = async (fileId) => {
  try {
    const response = await apiFetch(
      `${process.env.REACT_APP_API_BASE_URL}/deleteFile`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          numeroFormulaire: formData.numeroFormulaire,
          section,
          fileId,
        }),
      }
    );

    if (!response.ok) {
      throw new Error("Erreur lors de la suppression du fichier");
    }

    let newFormData = { ...formData };
    let updatedPreviewUrls = { ...previewUrls };

    // Retrouver le fichier supprimé pour supprimer son preview
    let deletedFile;

    if (section === "Visa du Conseil") {
      deletedFile = (newFormData.visaConseil.files || []).find(
        (file) => file._id === fileId
      );

      newFormData.visaConseil = {
        ...newFormData.visaConseil,
        files: (newFormData.visaConseil.files || []).filter(
          (file) => file._id !== fileId
        ),
        fileInfos: (newFormData.visaConseil.fileInfos || []).filter(
          (info) => info.fileId !== fileId
        ),
      };
    } else if (section === "407 ETR") {
      deletedFile = (newFormData.ETRFiles || []).find(
        (file) => file._id === fileId
      );

      newFormData.ETRFiles = (newFormData.ETRFiles || []).filter(
        (file) => file._id !== fileId
      );

      newFormData.ETRFileInfos = (newFormData.ETRFileInfos || []).filter(
        (info) => info.fileId !== fileId
      );
    } else if (section === "formation") {
      deletedFile = (newFormData.formation.files || []).find(
        (file) => file._id === fileId
      );

      newFormData.formation = {
        ...newFormData.formation,
        files: (newFormData.formation.files || []).filter(
          (file) => file._id !== fileId
        ),
        fileInfos: (newFormData.formation.fileInfos || []).filter(
          (info) => info.fileId !== fileId
        ),
      };
    }

    // Supprimer la preview
    if (deletedFile?.name) {
      delete updatedPreviewUrls[deletedFile.name];
    }

    setPreviewUrls(updatedPreviewUrls);
    setFormData(newFormData);
  } catch (err) {
    console.error(err);
    alert("Impossible de supprimer le fichier.");
  }
};

  const isCurrentTraitant =
    formData.etatDemande === "En traitement" &&
    formData.traitant?.at(-1)?.userId === user.id &&
    page === "Mes taches";

  const isOwnerEditing =
    (formData.etatDemande === "Brouillon" ||
      formData.etatDemande === "Rejeté") &&
    formData.userId === user.id &&
    page !== "Mes taches";

  const isAdminEditing =
    !isReadOnly &&
    formData.etatDemande === "En traitement" &&
    page === "Mes taches";

  const canUploadFile = isOwnerEditing || isCurrentTraitant || isAdminEditing;
  const fileInputId = `fileUpload-${section.replace(/[^a-zA-Z0-9]/g, "-")}`;

  return (
    <div className="mt-4 w-full max-w-md">
      <label
        htmlFor={fileInputId}
        className="mb-2 block text-sm font-medium leading-5 text-slate-700"
      >
        {section === "Visa du Conseil" && (
          <>
            Veuillez joindre une copie de votre relevé de Visa
            <span className="ml-1 text-red-500">*</span> :
          </>
        )}
        {section === "407 ETR" && (
          <>
            Veuillez joindre une copie de votre facture de 407 ETR
            <span className="ml-1 text-red-500">*</span> :
          </>
        )}
        {section === "formation" && (
          <>
            Veuillez joindre une copie du formulaire approuvé de demande de
            participation<span className="ml-1 text-red-500">*</span> :
          </>
        )}
      </label>
      {canUploadFile && (
        <div>
          <div className="relative block w-full">
            <input
              type="file"
              id={fileInputId}
              name={fileInputId}
              accept=".pdf,.jpg,.jpeg,.png"
              multiple // Permet l'upload multiple
              onChange={(e) => handleFileUpload(e)}
              className="absolute inset-0 opacity-0 cursor-pointer"
              disabled={!canUploadFile}
            />
            <button type="button" className="flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-lg border border-blue-500 bg-white px-5 text-sm font-medium text-blue-700 shadow-sm transition hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-200">
              <Paperclip className="h-4 w-4" />
              Joindre des fichiers
            </button>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Formats acceptés : PDF, JPG, JPEG, PNG.
          </p>
          {section === "Visa du Conseil" && renderError("Visafiles")}
          {section === "407 ETR" && renderError("ETRfiles")}
          {section === "formation" && renderError("Formationfiles")}
          {fileUploadError && (
            <p style={{ color: "red", marginTop: "0.5rem" }}>
              {fileUploadError}
            </p>
          )}
        </div>
      )}

      {section === "Visa du Conseil" &&
        formData.visaConseil?.files?.length > 0 && (
          <div className="mt-3 space-y-2">
            {formData.visaConseil.files.map((file) => {
              const canDeleteFile =
                isOwnerEditing ||
                isAdminEditing ||
                (isCurrentTraitant &&
                  formData.visaConseil.fileInfos?.some(
                    (info) =>
                      info.fileId === file._id && info.userId === user.id,
                  ));

              const filename = file.name || file.filename || "";
              const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(filename);
              const isPdf = /\.pdf$/i.test(filename);

              return (
                <div
                  key={filename}
                  className="flex w-full flex-wrap items-center gap-3 rounded-xl border border-slate-200 border-l-4 border-l-slate-400 bg-white p-2.5 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 sm:flex-nowrap"
                >
                  <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-slate-100 text-sm">
                    {isImage ? "🖼️" : isPdf ? "📄" : "📎"}
                  </span>

                  <button
                    type="button"
                    onClick={() => {
                      setSelectedFile(file);
                      setShowPopup(true);
                    }}
                    className="group flex min-w-0 flex-1 items-center gap-3 rounded-lg p-1 text-left outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-slate-800 group-hover:text-slate-950">{filename}</span>
                      <span className="block text-xs text-slate-500">Cliquer pour visualiser</span>
                    </span>
                    <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600 transition group-hover:bg-slate-200">
                      <Eye className="h-4 w-4" />
                    </span>
                  </button>

                  {canDeleteFile && (
                    <button
                      onClick={() => handleFileDelete(file._id)}
                      className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-red-200 bg-white text-red-600 shadow-sm transition hover:border-red-300 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-100"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

      {section === "407 ETR" && formData.ETRFiles?.length > 0 && (
        <div className="mt-3 space-y-2">
          {formData.ETRFiles.map((file) => {
            const canDeleteFile =
              isOwnerEditing ||
              isAdminEditing ||
              (isCurrentTraitant &&
                formData.ETRFileInfos?.some(
                  (info) => info.fileId === file._id && info.userId === user.id,
                ));

            const filename = file.name || file.filename || "";
            const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(filename);
            const isPdf = /\.pdf$/i.test(filename);

            return (
              <div
                key={filename}
                className="flex w-full flex-wrap items-center gap-3 rounded-xl border border-slate-200 border-l-4 border-l-slate-400 bg-white p-2.5 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 sm:flex-nowrap"
              >
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-slate-100 text-sm">
                  {isImage ? "🖼️" : isPdf ? "📄" : "📎"}
                </span>

                <button
                  type="button"
                  onClick={() => {
                    setSelectedFile(file);
                    setShowPopup(true);
                  }}
                  className="group flex min-w-0 flex-1 items-center gap-3 rounded-lg p-1 text-left outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-slate-800 group-hover:text-slate-950">{filename}</span>
                    <span className="block text-xs text-slate-500">Cliquer pour visualiser</span>
                  </span>
                  <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600 transition group-hover:bg-slate-200">
                    <Eye className="h-4 w-4" />
                  </span>
                </button>

                {canDeleteFile && (
                  <button
                    onClick={() => handleFileDelete(file._id)}
                    className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-red-200 bg-white text-red-600 shadow-sm transition hover:border-red-300 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-100"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {section === "formation" && formData.formation?.files?.length > 0 && (
        <div className="mt-3 space-y-2">
          {formData.formation?.files?.map((file) => {
            const canDeleteFile =
              isOwnerEditing ||
              isAdminEditing ||
              (isCurrentTraitant &&
                formData.formation.fileInfos?.some(
                  (info) => info.fileId === file._id && info.userId === user.id,
                ));

            const filename = file.name || file.filename || "";
            const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(filename);
            const isPdf = /\.pdf$/i.test(filename);

            return (
              <div
                key={filename}
                className="flex w-full flex-wrap items-center gap-3 rounded-xl border border-slate-200 border-l-4 border-l-slate-400 bg-white p-2.5 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 sm:flex-nowrap"
              >
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-slate-100 text-sm">
                  {isImage ? "🖼️" : isPdf ? "📄" : "📎"}
                </span>

                <button
                  type="button"
                  onClick={() => {
                    setSelectedFile(file);
                    setShowPopup(true);
                  }}
                  className="group flex min-w-0 flex-1 items-center gap-3 rounded-lg p-1 text-left outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-slate-800 group-hover:text-slate-950">{filename}</span>
                    <span className="block text-xs text-slate-500">Cliquer pour visualiser</span>
                  </span>
                  <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600 transition group-hover:bg-slate-200">
                    <Eye className="h-4 w-4" />
                  </span>
                </button>

                {canDeleteFile && (
                  <button
                    onClick={() => handleFileDelete(file._id)}
                    className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-red-200 bg-white text-red-600 shadow-sm transition hover:border-red-300 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-100"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Popup pour la prévisualisation */}
      {showPopup && selectedFile && (
        <FilePreviewModal
          selectedFile={selectedFile}
          fileUrl={fileUrl}
          onClose={() => setShowPopup(false)}
        />
      )}
    </div>
  );
};

export default Files;

// import React, { useState } from "react";
// import { TrashIcon } from "@heroicons/react/solid";
// import { SlideshowLightbox } from 'lightbox.js-react'
// import Resizer from 'react-image-file-resizer';
// import { PDFDocument } from 'pdf-lib';
// import { FilePreviewModal } from "./FilePreviewModal";
// const Files = ({ renderError, formData, setFormData, section, isReadOnly }) => {
//   const [previewUrls, setPreviewUrls] = useState({});
//   const [showPopup, setShowPopup] = useState(false);
//   const [selectedFile, setSelectedFile] = useState(null);
//   const fileUrl = selectedFile ? URL.createObjectURL(selectedFile) : "";
//   const [fileUploadError, setFileUploadError] = useState('');

//   const handleFileUpload = async (e) => {
//     const MAX_FILE_SIZE_MB = 5;
//     const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;
//     const files = Array.from(e.target.files) || [];

// const ALLOWED_TYPES = [
//   "application/pdf",
//   "image/jpeg",
//   "image/jpg",
//   "image/png"
// ];

// const invalidFiles = files.filter(file => !ALLOWED_TYPES.includes(file.type));

// if (invalidFiles.length > 0) {
//   setFileUploadError("Formats acceptés : PDF, JPG, JPEG, PNG.");
//   return;
// }

//     const resizeImage = (file) =>
//       new Promise((resolve) => {
//         if (!file.type.includes('image')) return resolve(file); // Skip non-images

//         Resizer.imageFileResizer(
//           file,
//           1600,          // maxWidth
//           1600,          // maxHeight
//           'WEBP',        // format de sortie
//           70,            // qualité (70% pour captures écrans)
//           0,             // rotation
//           (uri) => {
//             const resizedFile = new File([uri], file.name, { type: 'image/webp' });
//             resolve(resizedFile);
//           },
//           'blob',
//           200,          // minWidth
//           200            // minHeight
//         );
//       });

//     const compressPDF = async (file) => {
//       // Vérifie que c’est bien un PDF
//       if (file.type !== 'application/pdf') return file;

//       try {
//         const arrayBuffer = await file.arrayBuffer();

//         // Tente de charger le PDF (même s’il est chiffré)
//         const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });

//         // Vérifie si le PDF contient une signature (à éviter de modifier)
//         const hasSignature = pdfDoc.context.lookup(pdfDoc.catalog?.acroForm)?.has('SigFlags');

//         if (hasSignature) {
//           console.warn('PDF signé détecté — compression ignorée pour préserver la signature.');
//           return file; // on retourne le fichier original non modifié
//         }

//         // Sinon, compression légère
//         const compressedBytes = await pdfDoc.save();
//         return new File([compressedBytes], file.name, { type: 'application/pdf' });
//       } catch (error) {
//         console.error('Erreur lors du traitement du PDF :', error);
//         // Si erreur (PDF illisible ou signé), on retourne le fichier original
//         return file;
//       }
//     };

//     // Resize/compress based on file type
//     const processFile = async (file) => {
//       if (file.type.includes('image')) {
//         return await resizeImage(file);
//       } else if (file.type === 'application/pdf') {
//         return await compressPDF(file);
//       }
//       return file;
//     };

//     const processedFiles = await Promise.all(files.map(processFile));

//     // ✅ Vérification après traitement
//     const oversizedFiles = processedFiles.filter(file => file.size > MAX_FILE_SIZE);
//     if (oversizedFiles.length > 0) {
//       setFileUploadError(`Le fichier (même après compression) dépasse ${MAX_FILE_SIZE_MB} Mo.`);
//       return;
//     }

//     // 🗃️ Stockage dans le formData
//     let newFormData = { ...formData };
//     let newPreviewUrls = { ...previewUrls };

//     const filterDuplicates = (existingFiles = [], newFiles) => {
//       const existingFileNames = new Set(existingFiles.map(f => f.name || f.filename));
//       const filteredFiles = newFiles.filter(f => !existingFileNames.has(f.name));
//       if (filteredFiles.length < newFiles.length) {
//         setFileUploadError("Un fichier avec ce nom existe déjà.");
//       } else {
//         setFileUploadError("");
//       }
//       return filteredFiles;
//     };

//     if (section === "Visa du Conseil") {
//       const existing = newFormData.visaConseil?.files || [];
//       const filtered = filterDuplicates(existing, processedFiles);
//       newFormData.visaConseil = {
//         ...newFormData.visaConseil,
//         files: [...existing, ...filtered]
//       };
//       filtered.forEach(f => newPreviewUrls[f.name] = URL.createObjectURL(f));
//     } else if (section === "407 ETR") {
//       const existing = newFormData.ETRFiles || [];
//       const filtered = filterDuplicates(existing, processedFiles);
//       newFormData.ETRFiles = [...existing, ...filtered];
//       filtered.forEach(f => newPreviewUrls[f.name] = URL.createObjectURL(f));
//     } else if (section === "formation") {
//       const existing = newFormData.formation?.files || [];
//       const filtered = filterDuplicates(existing, processedFiles);
//       newFormData.formation = {
//         ...newFormData.formation,
//         files: [...existing, ...filtered]
//       };
//       filtered.forEach(f => newPreviewUrls[f.name] = URL.createObjectURL(f));
//     }

//     setPreviewUrls(newPreviewUrls);
//     setFormData(newFormData);
//   };

//   const handleFileDelete = (fileName) => {
//     let newFormData = { ...formData }; // Copier formData
//     let updatedPreviewUrls = { ...previewUrls }; // Copier previewUrls

//     if (section === "Visa du Conseil") {
//       if (newFormData.visaConseil.files) {
//         newFormData.visaConseil.files = newFormData.visaConseil.files.filter(file => file.name !== fileName);
//       }
//     }
//     else if (section === "407 ETR") {
//       if (newFormData.ETRFiles) {
//         newFormData.ETRFiles = newFormData.ETRFiles.filter(file => file.name !== fileName);
//       }
//     } else if (section === "formation") {
//       if (newFormData.formation.files) {
//         newFormData.formation.files = newFormData.formation.files.filter(file => file.name !== fileName);
//       }
//     }

//     delete updatedPreviewUrls[fileName]; // Supprimer l'URL de prévisualisation

//     setPreviewUrls(updatedPreviewUrls);
//     setFormData(newFormData);
//   };

//   return (

//     <div className="w-3/5 mt-4">
//       <label
//         htmlFor="fileUpload"
//         className="block text-sm font-medium text-blue-900 mb-2"
//       >
//         {section === "Visa du Conseil" && (
//           <>
//             Veuillez joindre une copie de votre relevé de Visa<span className="text-xl text-red-900">*</span> :
//           </>
//         )}
//         {section === "407 ETR" && (
//           <>
//             Veuillez joindre une copie de votre facture de 407 ETR<span className="text-xl text-red-900">*</span> :
//           </>
//         )}
//         {section === "formation" && (
//           <>
//             Veuillez joindre une copie du formulaire approuvé de demande de participation<span className="text-xl text-red-900">*</span> :
//           </>
//         )}
//       </label>
//       {/* { isReadOnly === false && ( */}
//       <div>
//         <div className="relative">
//           <input
//             type="file"
//             id="fileUpload"
//             name="fileUpload"
//             accept=".pdf,.jpg,.jpeg,.png"
//             multiple // Permet l'upload multiple
//             onChange={(e) => handleFileUpload(e)}
//             className="absolute inset-0 opacity-0 cursor-pointer"
//           />
//           <button className="w-full px-4 py-2 border border-blue-500 rounded-lg text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 cursor-pointer">
//             Choisir des fichiers
//           </button>
//         </div>
//         <p className="text-sm text-gray-500 mt-1">
//           Formats acceptés : PDF, JPG, JPEG, PNG.
//         </p>
//         {section === "Visa du Conseil" && renderError("Visafiles")}
//         {section === "407 ETR" && renderError("ETRfiles")}
//         {section === "formation" && renderError("Formationfiles")}
//         {fileUploadError && (
//           <p style={{ color: 'red', marginTop: '0.5rem' }}>{fileUploadError}</p>
//         )}
//       </div>
//       {/*  )} */}

//       {section === "Visa du Conseil" && formData.visaConseil?.files?.length > 0 && (
//   <div className="mt-3 space-y-2">
//     {formData.visaConseil.files.map((file) => {
//       const filename = file.name || file.filename || "";
//       const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(filename);
//       const isPdf = /\.pdf$/i.test(filename);

//       return (
//         <div
//           key={filename}
//           className="flex items-center gap-3 border border-gray-200 rounded-lg px-3 py-2 bg-white hover:bg-blue-50 transition-colors duration-150"
//         >
//           <span className="text-lg flex-shrink-0">
//             {isImage ? "🖼️" : isPdf ? "📄" : "📎"}
//           </span>

//           <button
//             onClick={() => {
//               setSelectedFile(file);
//               setShowPopup(true);
//             }}
//             className="flex-1 min-w-0 text-left group"
//           >
//             <p className="text-sm font-medium text-blue-800 group-hover:text-blue-600 truncate">
//               {filename}
//             </p>
//             <p className="text-xs text-gray-400">
//               Cliquer pour visualiser
//             </p>
//           </button>

//           <span className="text-blue-400 text-xs flex-shrink-0">↗</span>

//           {(!file._id || formData.etatDemande === "Brouillon") && (
//             <button
//               onClick={() => handleFileDelete(file.name)}
//               className="w-9 h-9 border border-red-500 text-red-600 rounded-lg flex items-center justify-center transition duration-200 hover:bg-red-50 flex-shrink-0"
//             >
//               <TrashIcon className="w-5 h-5" />
//             </button>
//           )}
//         </div>
//       );
//     })}
//   </div>
// )}

//       {section === "407 ETR" && formData.ETRFiles?.length > 0 && (
//   <div className="mt-3 space-y-2">
//     {formData.ETRFiles.map((file) => {
//       const filename = file.name || file.filename || "";
//       const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(filename);
//       const isPdf = /\.pdf$/i.test(filename);

//       return (
//         <div
//           key={filename}
//           className="flex items-center gap-3 border border-gray-200 rounded-lg px-3 py-2 bg-white hover:bg-blue-50 transition-colors duration-150"
//         >
//           <span className="text-lg flex-shrink-0">
//             {isImage ? "🖼️" : isPdf ? "📄" : "📎"}
//           </span>

//           <button
//             onClick={() => {
//               setSelectedFile(file);
//               setShowPopup(true);
//             }}
//             className="flex-1 min-w-0 text-left group"
//           >
//             <p className="text-sm font-medium text-blue-800 group-hover:text-blue-600 truncate">
//               {filename}
//             </p>
//             <p className="text-xs text-gray-400">
//               Cliquer pour visualiser
//             </p>
//           </button>

//           <span className="text-blue-400 text-xs flex-shrink-0">↗</span>

//           {(!file._id || formData.etatDemande === "Brouillon") && (
//             <button
//               onClick={() => handleFileDelete(file.name)}
//               className="w-9 h-9 border border-red-500 text-red-600 rounded-lg flex items-center justify-center transition duration-200 hover:bg-red-50 flex-shrink-0"
//             >
//               <TrashIcon className="w-5 h-5" />
//             </button>
//           )}
//         </div>
//       );
//     })}
//   </div>
// )}

//       {section === "formation" && formData.formation?.files?.length > 0 && (
//   <div className="mt-3 space-y-2">
//     {formData.formation?.files?.map((file) => {
//       const filename = file.name || file.filename || "";
//       const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(filename);
//       const isPdf = /\.pdf$/i.test(filename);

//       return (
//         <div
//           key={filename}
//           className="flex items-center gap-3 border border-gray-200 rounded-lg px-3 py-2 bg-white hover:bg-blue-50 transition-colors duration-150"
//         >
//           <span className="text-lg flex-shrink-0">
//             {isImage ? "🖼️" : isPdf ? "📄" : "📎"}
//           </span>

//           <button
//             onClick={() => {
//               setSelectedFile(file);
//               setShowPopup(true);
//             }}
//             className="flex-1 min-w-0 text-left group"
//           >
//             <p className="text-sm font-medium text-blue-800 group-hover:text-blue-600 truncate">
//               {filename}
//             </p>
//             <p className="text-xs text-gray-400">
//               Cliquer pour visualiser
//             </p>
//           </button>

//           <span className="text-blue-400 text-xs flex-shrink-0">↗</span>

//           {(!file._id || formData.etatDemande === "Brouillon") && (
//             <button
//               onClick={() => handleFileDelete(file.name)}
//               className="w-9 h-9 border border-red-500 text-red-600 rounded-lg flex items-center justify-center transition duration-200 hover:bg-red-50 flex-shrink-0"
//             >
//               <TrashIcon className="w-5 h-5" />
//             </button>
//           )}
//         </div>
//       );
//     })}
//   </div>
// )}

//       {/* Popup pour la prévisualisation */}
//       {showPopup && selectedFile && (
//         <FilePreviewModal
//           selectedFile={selectedFile}
//           fileUrl={fileUrl}
//           onClose={() => setShowPopup(false)}
//         />

//       )}
//     </div>
//   )
// }

// export default Files
